'use client';

import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import CmsImage from '@/components/ui/CmsImage';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import CompactPageHero from '@/components/sections/shared/CompactPageHero';
import { getImageUrlForVariant, stripHtml } from '@/lib/homepage-mappers';
import CmsRichTextHeading from '@/components/ui/CmsRichTextHeading';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import type { AwardsButton, TransformedAwardsData } from '@/types/awards';

gsap.registerPlugin(ScrollTrigger);

interface AwardsTimelineClientProps {
  locale: string;
  awardsData: TransformedAwardsData | null;
}

type AwardItem = {
  id: string;
  title: string;
  description: string;
  image: string;
  url: string | null;
  awardTypes: string[];
  year: string;
};

const MARQUEE_COUNT = 6;

const TICKER_ITEMS = [
  'Quality & Operations',
  'Brand & Innovation',
  'Education & CSR',
  'Safety',
  'Sustainability',
  'Wonder Cement',
  'Quality & Operations',
  'Brand & Innovation',
  'Education & CSR',
  'Safety',
  'Sustainability',
  'Wonder Cement',
];

const AWARD_IMAGE_POOL = [
  '/images/guid_gr.jpg',
  '/images/guid_343.png',
  '/images/guid_341.png',
  '/images/guid_342.png',
  '/images/guild_351.jpg',
  '/images/stand_socil1.png',
  '/images/stand_env.png',
  '/images/dealL_img.png',
  '/images/coast_img1.png',
  '/images/coast_img2.png',
  '/images/home_img1.png',
  '/images/home_img3.png',
  '/images/building.png',
];

function fallbackImage(yearIdx: number, itemIdx: number): string {
  return AWARD_IMAGE_POOL[(yearIdx * 7 + itemIdx) % AWARD_IMAGE_POOL.length];
}

const FALLBACK_HEADING = 'The Evidence of Excellence';
const FALLBACK_SUBHEADING_HTML =
  '<p>A curated timeline of recognitions that reflect our commitment to quality, trust, and performance.</p>';
const FALLBACK_PREFOOTER_HEADING = 'Award-winning quality, verified by the industry';
const FALLBACK_PREFOOTER_BG = '#E31E24';
const FALLBACK_PREFOOTER_IMAGE = '/images/awads_am.png';

const SELECT_STYLE: React.CSSProperties = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23A09A93' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10px center',
};

function buttonHref(button: AwardsButton | undefined, locale: string, fallback: string): string {
  if (!button) return `/${locale}${fallback}`;
  const url = button.buttonUrl;
  if (!url) return `/${locale}${fallback}`;
  if (/^https?:\/\//.test(url)) return url;
  const trimmed = url.startsWith('/') ? url : `/${url}`;
  return `/${locale}${trimmed}`;
}

function AwardCard({ item, priority = false }: { item: AwardItem; priority?: boolean }) {
  const hasLink = Boolean(item.url);

  const card = (
    <div className="group flex h-full flex-col overflow-hidden rounded-xl border border-[#e4e4e4] bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
      <div className="relative aspect-[16/9] shrink-0 overflow-hidden bg-neutral-100">
        <CmsImage
          src={item.image}
          alt={item.title}
          fill
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          className="object-cover object-center transition-transform duration-700 group-hover:scale-[1.05]"
          priority={priority}
          fallbackSrc={MEDIA_PLACEHOLDER}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
        <div className="absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 bg-[#E31E24] transition-transform duration-300 group-hover:scale-x-100" />
        <span className="absolute left-3 top-3 inline-flex items-center rounded-full bg-[#1C1917]/75 px-2.5 py-[5px] text-[9px] font-figtree font-bold uppercase tracking-[0.12em] text-white backdrop-blur-sm">
          {item.year}
        </span>
      </div>
      <div className="flex min-h-[148px] flex-1 flex-col gap-2 px-4 py-4 sm:min-h-[156px]">
        <span className="inline-flex self-start items-center rounded-full border border-[#e4e4e4] px-2.5 py-[4px] text-[8px] font-figtree font-bold uppercase tracking-[0.14em] text-[#A09A93]">
          {item.awardTypes[0] || 'Other Awards'}
        </span>
        <h3 className="line-clamp-2 flex-1 font-figtree text-[0.875rem] font-semibold leading-[1.4] text-[#1C1917] sm:text-[0.9375rem]">
          {item.title}
        </h3>
        {item.description ? (
          <p className="line-clamp-2 text-[11px] font-figtree leading-relaxed text-[#A09A93]">
            {item.description}
          </p>
        ) : null}
        {hasLink ? (
          <div className="mt-auto border-t border-[#f0f0f0] pt-2">
            <span className="text-[9px] font-figtree font-bold uppercase tracking-[0.14em] text-[#E31E24] opacity-0 transition-all duration-200 translate-x-2 group-hover:translate-x-0 group-hover:opacity-100">
              Learn more →
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );

  if (hasLink) {
    return (
      <a
        href={item.url!}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={item.title}
        className="block h-full cursor-pointer"
      >
        {card}
      </a>
    );
  }

  return <div className="h-full">{card}</div>;
}

/** Filtered awards grid, or an empty-state with a "clear filters" action. */
function FilteredAwardsView({
  filteredAwards,
  onClearFilters,
}: {
  filteredAwards: AwardItem[];
  onClearFilters: () => void;
}) {
  if (filteredAwards.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12 text-center sm:py-14">
        <div className="w-12 h-12 rounded-full border border-[#e4e4e4] bg-white flex items-center justify-center">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#A09A93"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
        </div>
        <p className="font-figtree text-[0.9375rem] font-semibold text-[#1C1917]">
          No awards match your filters
        </p>
        <button
          onClick={onClearFilters}
          className="text-[12px] font-figtree font-medium text-[#E31E24] underline underline-offset-2"
        >
          Clear all filters
        </button>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3">
      {filteredAwards.map((item, i) => (
        <article key={item.id} className="h-full">
          <AwardCard item={item} priority={i === 0} />
        </article>
      ))}
    </div>
  );
}

/** Whether an award passes the active search + year + category filters. */
function awardMatchesFilters(
  item: AwardItem,
  search: string,
  yearFilter: string,
  categoryFilter: string,
): boolean {
  const q = search.trim().toLowerCase();
  if (q && !item.title.toLowerCase().includes(q) && !item.description.toLowerCase().includes(q)) {
    return false;
  }
  if (yearFilter && item.year !== yearFilter) return false;
  if (categoryFilter && !item.awardTypes.includes(categoryFilter)) return false;
  return true;
}

/** Page-load + scroll-triggered entrance animations for the awards page. */
function runAwardsIntroAnimations() {
  // Hero entrance
  gsap.from('[data-anim="hero-h1"]', {
    y: 48,
    opacity: 0,
    duration: 1,
    ease: 'power4.out',
    delay: 0.1,
    immediateRender: false,
  });
  gsap.from('[data-anim="hero-sub"]', {
    y: 24,
    opacity: 0,
    duration: 0.75,
    ease: 'power3.out',
    delay: 0.3,
    immediateRender: false,
  });

  // Filter bar
  gsap.from('[data-anim="filter-bar"]', {
    y: 32,
    opacity: 0,
    duration: 0.7,
    ease: 'power3.out',
    immediateRender: false,
    scrollTrigger: { trigger: '[data-anim="filter-bar"]', start: 'top 88%', once: true },
  });

  // Marquee section label rule
  gsap.from('[data-anim="marquee-rule"]', {
    scaleX: 0,
    transformOrigin: 'left center',
    duration: 1,
    ease: 'expo.out',
    immediateRender: false,
    scrollTrigger: { trigger: '[data-anim="marquee-rule"]', start: 'top 88%', once: true },
  });

  // Marquee cards stagger
  gsap.from('[data-anim="marquee-card"]', {
    y: 48,
    opacity: 0,
    duration: 0.65,
    ease: 'power3.out',
    stagger: 0.09,
    immediateRender: false,
    scrollTrigger: { trigger: '[data-anim="marquee-grid"]', start: 'top 85%', once: true },
  });

  // Expander button
  gsap.from('[data-anim="expander-btn"]', {
    y: 24,
    opacity: 0,
    duration: 0.6,
    ease: 'power3.out',
    immediateRender: false,
    scrollTrigger: { trigger: '[data-anim="expander-btn"]', start: 'top 90%', once: true },
  });

  // CTA
  gsap.from('[data-anim="cta"]', {
    y: 36,
    opacity: 0,
    duration: 0.8,
    ease: 'power3.out',
    immediateRender: false,
    scrollTrigger: { trigger: '[data-anim="cta"]', start: 'top 88%', once: true },
  });
}

/** Entrance animation for the expanded award cards + year labels. */
function runAwardsExpandAnimations() {
  gsap.from('[data-anim="expand-card"]', {
    y: 36,
    opacity: 0,
    duration: 0.55,
    ease: 'power3.out',
    stagger: 0.06,
    immediateRender: false,
  });
  gsap.from('[data-anim="expand-year"]', {
    x: -20,
    opacity: 0,
    duration: 0.5,
    ease: 'power3.out',
    stagger: 0.1,
    immediateRender: false,
  });
}

export default function AwardsTimelineClient({ locale, awardsData }: AwardsTimelineClientProps) {
  const [search, setSearch] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showAll, setShowAll] = useState(false);

  const mainRef = useRef<HTMLElement>(null);
  const expandRef = useRef<HTMLDivElement>(null);

  const heroHeading = awardsData?.heading || FALLBACK_HEADING;
  const heroSubHeadingHtml = awardsData?.subHeadingHtml || FALLBACK_SUBHEADING_HTML;

  const cmsTickerLabels = (awardsData?.ticker ?? [])
    .map((item) => item.heading?.trim())
    .filter((label): label is string => Boolean(label));

  const tickerLabels =
    cmsTickerLabels.length > 0 ? [...cmsTickerLabels, ...cmsTickerLabels] : TICKER_ITEMS;

  // ── Data ─────────────────────────────────────────────────
  const allAwards = useMemo<AwardItem[]>(() => {
    const sourceYears = awardsData?.years ?? [];
    return sourceYears.flatMap((group, yearIdx) =>
      (group.tabItem ?? []).map((item, itemIdx) => ({
        id: `${group.id}-${item.id}`,
        title: item.heading,
        description: stripHtml(item.description ?? ''),
        image: getImageUrlForVariant(item.media, 'large') || fallbackImage(yearIdx, itemIdx),
        url: item.urlOptional,
        awardTypes: (item.awardType ?? [])
          .map((type) => type.awardTitle?.trim())
          .filter((type): type is string => Boolean(type)),
        year: group.heading,
      })),
    );
  }, [awardsData]);

  const uniqueYears = useMemo(() => Array.from(new Set(allAwards.map((a) => a.year))), [allAwards]);

  const categoryOptions = useMemo(
    () => Array.from(new Set(allAwards.flatMap((award) => award.awardTypes))),
    [allAwards],
  );

  const filteredAwards = useMemo(
    () => allAwards.filter((item) => awardMatchesFilters(item, search, yearFilter, categoryFilter)),
    [allAwards, search, yearFilter, categoryFilter],
  );

  const remainingByYear = useMemo(() => {
    const tail = allAwards.slice(MARQUEE_COUNT);
    const map = new Map<string, AwardItem[]>();
    tail.forEach((item) => {
      if (!map.has(item.year)) map.set(item.year, []);
      map.get(item.year)!.push(item);
    });
    return Array.from(map.entries()).map(([year, items]) => ({ year, items }));
  }, [allAwards]);

  const marqueeAwards = allAwards.slice(0, MARQUEE_COUNT);
  const tailCount = allAwards.length - MARQUEE_COUNT;
  const activeFilterCount = [search, yearFilter, categoryFilter].filter(Boolean).length;
  const isFiltering = activeFilterCount > 0;

  function clearFilters() {
    setSearch('');
    setYearFilter('');
    setCategoryFilter('');
  }

  const renderFilteredView = () => (
    <FilteredAwardsView filteredAwards={filteredAwards} onClearFilters={clearFilters} />
  );

  // ── GSAP — page load + scroll animations ─────────────────
  useLayoutEffect(() => {
    const ctx = gsap.context(runAwardsIntroAnimations, mainRef);
    return () => ctx.revert();
  }, []);

  // ── GSAP — expanded cards animate in ─────────────────────
  useLayoutEffect(() => {
    if (!showAll || !expandRef.current) return;
    const ctx = gsap.context(runAwardsExpandAnimations, expandRef);
    return () => ctx.revert();
  }, [showAll]);

  // ── Prefooter data ────────────────────────────────────────
  const prefooter = awardsData?.prefooter ?? null;
  const prefooterHeading = prefooter?.heading || FALLBACK_PREFOOTER_HEADING;
  const prefooterBg = prefooter?.bgColor || FALLBACK_PREFOOTER_BG;
  const prefooterImageUrl =
    getImageUrlForVariant(prefooter?.image, 'large') || FALLBACK_PREFOOTER_IMAGE;
  const prefooterImageAlt = prefooter?.image?.alternativeText || 'Awards section visual';
  const primaryBtn = prefooter?.buttonWhite?.[0];
  const secondaryBtn = prefooter?.buttonWhite?.[1];

  return (
    <main ref={mainRef} className="text-[#1C1917]">
      <style>{`
        @keyframes awards-ticker {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .awards-ticker-track {
          animation: awards-ticker 28s linear infinite;
          will-change: transform;
        }
        .awards-ticker-track:hover { animation-play-state: paused; }
      `}</style>

      <CompactPageHero>
        <CmsRichTextHeading
          data-anim="hero-h1"
          className="anim-h1 hero-title text-white"
          html={awardsData?.titleRichText}
          fallback={heroHeading}
        />
        <div
          data-anim="hero-sub"
          className="mt-3 max-w-2xl hero-subtitle text-white/60 sm:mt-4 [&_p]:m-0"
          dangerouslySetInnerHTML={{ __html: heroSubHeadingHtml }}
        />
      </CompactPageHero>

      {/* ── TICKER ───────────────────────────────────────────── */}
      <div className="relative z-[1] overflow-hidden border-t border-white/10 bg-[#1C1917] py-2.5 select-none sm:py-3">
        <div className="awards-ticker-track flex items-center whitespace-nowrap">
          {tickerLabels.map((label, i) => (
            <span key={i} className="inline-flex items-center gap-5 px-5">
              <span className="text-[10px] font-figtree font-bold uppercase tracking-[0.22em] text-white/50">
                {label}
              </span>
              <span className="w-1 h-1 rounded-full bg-[#E31E24] shrink-0" />
            </span>
          ))}
        </div>
      </div>

      {/* ── CONTENT ───────────────────────────────────────────── */}
      <div className="bg-[#f3f3f3]">
        <div className="mx-auto container px-4 py-10 max-sm:px-4 sm:py-12 md:py-14">
          {/* ── FILTER BAR ──────────────────────────────────────── */}
          <div
            data-anim="filter-bar"
            className="mb-5 rounded-2xl border border-[#e4e4e4] bg-white px-4 py-4 shadow-sm sm:mb-6 sm:px-5"
          >
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center">
                <div className="relative min-w-0 flex-1">
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#A09A93]"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search award name..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 border border-[#e4e4e4] rounded-xl
                               text-[12px] font-figtree text-[#1C1917] placeholder:text-[#A09A93]
                               focus:outline-none focus:border-[#E31E24] focus:ring-1 focus:ring-[#E31E24]/20
                               transition-colors bg-white"
                  />
                </div>
                {uniqueYears.length > 0 && (
                  <select
                    value={yearFilter}
                    onChange={(e) => setYearFilter(e.target.value)}
                    style={SELECT_STYLE}
                    className="w-full appearance-none rounded-xl border border-[#e4e4e4] bg-white px-4 py-2.5 pr-7 text-[12px] font-figtree font-medium text-[#1C1917] transition-colors focus:border-[#E31E24] focus:outline-none focus:ring-1 focus:ring-[#E31E24]/20 md:w-auto md:min-w-[132px]"
                  >
                    <option value="">All Years</option>
                    {uniqueYears.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                )}
                <div className="flex flex-wrap items-center gap-3 md:shrink-0">
                  <span className="text-[11px] font-figtree font-medium text-[#A09A93]">
                    {isFiltering
                      ? `${filteredAwards.length} of ${allAwards.length} awards`
                      : `${allAwards.length} awards`}
                  </span>
                  {activeFilterCount > 0 && (
                    <button
                      onClick={clearFilters}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-figtree font-semibold
                                 text-[#E31E24] border border-[#E31E24]/20 bg-[#E31E24]/5
                                 hover:bg-[#E31E24]/10 transition-colors whitespace-nowrap"
                    >
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                      >
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                      Clear ({activeFilterCount})
                    </button>
                  )}
                </div>
              </div>
              <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] sm:flex-wrap sm:overflow-visible sm:pb-0 [&::-webkit-scrollbar]:hidden">
                <button
                  onClick={() => setCategoryFilter('')}
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-[11px] font-figtree font-medium leading-none transition-all ${
                    categoryFilter === ''
                      ? 'border-[#1C1917] bg-[#1C1917] text-white'
                      : 'border-[#e4e4e4] text-[#4A4540] hover:border-[#1C1917] hover:text-[#1C1917]'
                  }`}
                >
                  All
                </button>
                {categoryOptions.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setCategoryFilter(categoryFilter === cat ? '' : cat)}
                    className={`shrink-0 rounded-full border px-3 py-1.5 text-[11px] font-figtree font-medium leading-none transition-all ${
                      categoryFilter === cat
                        ? 'border-[#E31E24] bg-[#E31E24] text-white'
                        : 'border-[#e4e4e4] text-[#4A4540] hover:border-[#E31E24] hover:text-[#E31E24]'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── FILTERED VIEW ───────────────────────────────────── */}
          {isFiltering ? (
            renderFilteredView()
          ) : (
            <>
              {/* ── MARQUEE AWARDS ──────────────────────────────── */}
              {marqueeAwards.length > 0 && (
                <div className="mb-5 sm:mb-6">
                  <div className="mb-3 flex flex-wrap items-center gap-3 sm:mb-4 sm:gap-4">
                    <span className="h-5 w-1 shrink-0 rounded-full bg-[#E31E24]" />
                    <span className="text-[9px] font-figtree font-bold uppercase tracking-[0.28em] text-[#A09A93]">
                      Featured Awards
                    </span>
                    <span
                      data-anim="marquee-rule"
                      className="hidden h-px flex-1 origin-left bg-[#e4e4e4] sm:block"
                    />
                    <span className="text-[9px] font-figtree text-[#A09A93] sm:ml-auto">
                      {marqueeAwards.length} featured
                    </span>
                  </div>

                  <div
                    data-anim="marquee-grid"
                    className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3"
                  >
                    {marqueeAwards.map((item, i) => (
                      <article key={item.id} data-anim="marquee-card" className="h-full">
                        <AwardCard item={item} priority={i < 3} />
                      </article>
                    ))}
                  </div>
                </div>
              )}

              {/* ── SHOW MORE EXPANDER ───────────────────────────── */}
              {tailCount > 0 && (
                <div>
                  <button
                    data-anim="expander-btn"
                    onClick={() => setShowAll((v) => !v)}
                    className="group flex w-full items-center justify-between gap-3 rounded-2xl border border-[#e4e4e4] bg-white px-4 py-3.5 shadow-sm transition-colors hover:border-[#1C1917] sm:gap-4 sm:px-6 sm:py-4"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-figtree font-semibold text-[0.9375rem] text-[#1C1917]">
                        {showAll ? 'Show less' : 'View all awards'}
                      </span>
                      {!showAll && (
                        <span
                          className="inline-flex items-center justify-center h-5 px-2 rounded-full
                                         bg-[#f3f3f3] border border-[#e4e4e4]
                                         text-[10px] font-figtree font-bold text-[#A09A93]"
                        >
                          +{tailCount} more
                        </span>
                      )}
                    </div>
                    <span
                      className="flex items-center justify-center w-8 h-8 rounded-xl
                                     border border-[#e4e4e4] text-[#A09A93]
                                     group-hover:border-[#1C1917] group-hover:text-[#1C1917] transition-colors shrink-0"
                    >
                      {showAll ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </span>
                  </button>

                  {showAll && (
                    <div ref={expandRef} className="mt-6 space-y-8 sm:space-y-10">
                      {remainingByYear.map((group) => (
                        <section key={group.year}>
                          <div
                            data-anim="expand-year"
                            className="mb-3 flex flex-wrap items-center gap-3 sm:mb-4 sm:gap-4"
                          >
                            <span className="h-5 w-1 shrink-0 rounded-full bg-[#E31E24]" />
                            <h2 className="font-figtree text-base font-bold text-[#1C1917] sm:text-lg">
                              {group.year}
                            </h2>
                            <span className="hidden h-px flex-1 bg-[#e4e4e4] sm:block" />
                            <span className="text-[11px] font-figtree font-medium text-[#A09A93] sm:ml-auto">
                              {group.items.length} {group.items.length === 1 ? 'award' : 'awards'}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3">
                            {group.items.map((item) => (
                              <article key={item.id} data-anim="expand-card" className="h-full">
                                <AwardCard item={item} />
                              </article>
                            ))}
                          </div>
                        </section>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── PREFOOTER CTA ─────────────────────────────────────── */}
        <div data-anim="cta" className="mx-auto container px-4 pb-10 max-sm:px-4 sm:pb-12 md:pb-14">
          <div
            className="relative overflow-hidden rounded-2xl px-5 sm:px-8 md:px-10"
            style={{ backgroundColor: prefooterBg }}
          >
            <div className="grid grid-cols-1 items-center gap-4 sm:gap-6 md:grid-cols-[1fr_280px] lg:grid-cols-[1fr_400px]">
              <div className="relative z-10 py-6 sm:py-8 md:py-10">
                <h3 className="max-w-xl font-figtree text-lg font-bold leading-tight text-white sm:text-2xl md:text-[1.75rem]">
                  {prefooterHeading}
                </h3>
                <div className="mt-6 flex flex-col gap-3 sm:mt-7 sm:flex-row sm:flex-wrap sm:items-center">
                  <SwapTextButton
                    text={primaryBtn?.buttonText || 'Enquire Now'}
                    hoverText={primaryBtn?.buttonText || 'Enquire Now'}
                    href={buttonHref(primaryBtn, locale, '/contact-us')}
                    {...getCmsButtonLinkProps(primaryBtn)}
                    tone="light"
                    showArrow={false}
                    className="min-h-[40px] w-full justify-center rounded-md px-4 py-2 text-xs font-semibold shadow-none sm:w-auto"
                    aria-label={primaryBtn?.ariaLabel || primaryBtn?.buttonText || 'Enquire now'}
                  />
                  <SwapTextButton
                    text={secondaryBtn?.buttonText || 'View Our Products'}
                    hoverText={secondaryBtn?.buttonText || 'View Our Products'}
                    href={buttonHref(secondaryBtn, locale, '/products')}
                    {...getCmsButtonLinkProps(secondaryBtn)}
                    variant="outline"
                    showArrow={false}
                    className="min-h-[40px] w-full justify-center rounded-md !border-white !bg-transparent px-4 py-2 text-xs font-semibold !text-white shadow-none hover:!bg-white hover:!text-[#1C1917] sm:w-auto"
                    aria-label={
                      secondaryBtn?.ariaLabel || secondaryBtn?.buttonText || 'View our products'
                    }
                  />
                </div>
              </div>
              <div className="relative min-h-[180px] sm:min-h-[220px] md:min-h-[240px]">
                <CmsImage
                  src={prefooterImageUrl}
                  alt={prefooterImageAlt}
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 320px, 400px"
                  className="object-contain object-center md:object-right-bottom"
                  fallbackSrc={MEDIA_PLACEHOLDER}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
